import { useEffect, useRef, useState } from 'react';
import { repere } from './zonesDuTuto';

/**
 * Où est une zone du tuto à l'écran, image par image (ticket 33).
 *
 * La zone suit le vrai élément : s'il bouge (la page défile, la carte glisse, une fiche
 * s'ouvre, la fenêtre change de taille), le rectangle suit. On le relit à chaque image tant
 * que le tuto est là : c'est un seul `getBoundingClientRect`, et c'est le seul moyen de
 * suivre ce que la caméra de la carte ou une animation déplacent sans le dire.
 */

/** Ce qu'on laisse autour d'une zone : dans le trou de lumière comme dans l'anneau. */
export const JOUR_AUTOUR = 6;

export interface Rectangle {
  x: number;
  y: number;
  largeur: number;
  hauteur: number;
}

function pareil(a: Rectangle | null, b: Rectangle | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.x - b.x) < 0.5 &&
    Math.abs(a.y - b.y) < 0.5 &&
    Math.abs(a.largeur - b.largeur) < 0.5 &&
    Math.abs(a.hauteur - b.hauteur) < 0.5
  );
}

/**
 * Le rectangle de la zone, et si c'est bien celle de l'étape. `cle` change à chaque étape :
 * le rectangle d'avant reste le temps de trouver le nouveau (le trou de lumière glisse de
 * l'un à l'autre au lieu de clignoter), mais `trouvee` repasse à faux. La première fois qu'on
 * trouve la zone d'une étape, on fait défiler la page pour qu'elle soit en vue.
 */
export function useZoneALEcran(
  trouver: (() => HTMLElement | null) | null,
  cle: string
): { rectangle: Rectangle | null; trouvee: boolean } {
  const [rectangle, setRectangle] = useState<Rectangle | null>(null);
  const [trouvee, setTrouvee] = useState(false);
  const dernier = useRef<Rectangle | null>(null);
  const trouverIci = useRef(trouver);
  trouverIci.current = trouver;

  const aSuivre = trouver !== null;
  useEffect(() => {
    let image = 0;
    let amene: HTMLElement | null = null;
    setTrouvee(false);
    // Rien à suivre (l'accueil, une étape sans zone) : pas de boucle.
    if (!aSuivre) return;

    const suivre = () => {
      const el = trouverIci.current?.() ?? null;
      let r: Rectangle | null = null;
      if (el) {
        const b = el.getBoundingClientRect();
        r = { x: b.left, y: b.top, largeur: b.width, hauteur: b.height };
        // Une fois par élément : l'anneau peut passer d'un geste à l'autre dans la même étape.
        if (amene !== el) {
          amene = el;
          if (b.top < 0 || b.bottom > window.innerHeight) {
            if (el.closest(`${repere('preparer')}, ${repere('fiche')}, [role="dialog"]`)) {
              // Dans un panneau qui défile (le bouton « Ranger » sous une longue liste, ticket
              // 17 de retours-testeurs) : c'est le panneau qui défile, pas la page.
              el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            } else {
              // Hors de la fenêtre (la page est plus haute qu'elle) : on la fait venir au milieu.
              const milieu = b.top - Math.max(16, (window.innerHeight - b.height) / 2);
              window.scrollBy({ top: milieu, behavior: 'smooth' });
            }
          }
        }
      }
      setTrouvee(r !== null);
      // Une zone qui disparaît (un clic qui change d'écran) laisse le trou où il était.
      if (r && !pareil(r, dernier.current)) {
        dernier.current = r;
        setRectangle(r);
      }
      image = requestAnimationFrame(suivre);
    };
    image = requestAnimationFrame(suivre);
    return () => cancelAnimationFrame(image);
  }, [cle, aSuivre]);

  return { rectangle, trouvee };
}
